/**
 * UserManagement Component
 *
 * Admin interface for managing MarkoTM organization users
 */
import React, { useState } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Users,
  Shield,
  UserPlus,
  Trash2,
  Mail,
  AlertCircle,
  Check,
  X,
  Loader2,
  Crown,
  RefreshCw
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../../common';
import { H3, Text } from '../../../common/Typography';
import { Button } from '../../../common';
import api from '../../../services/api';

// API functions
const fetchAdminUsers = async () => {
  const response = await api.get('/admin/users');
  return response.data;
};

const inviteUser = async ({ email, role }) => {
  const response = await api.post('/admin/users/invite', { email, role });
  return response.data;
};

const removeUser = async (userId) => {
  const response = await api.delete(`/admin/users/${userId}`);
  return response.data;
};

const updateUserRole = async ({ userId, role }) => {
  const response = await api.patch(`/admin/users/${userId}/role`, { role });
  return response.data;
};

const UserManagement = () => {
  const { user: currentUser } = useUser();
  const queryClient = useQueryClient();
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('org:member');
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [confirmRoleChange, setConfirmRoleChange] = useState(null);

  // Fetch users query
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['adminUsers'],
    queryFn: fetchAdminUsers
  });

  // Invite mutation
  const inviteMutation = useMutation({
    mutationFn: inviteUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminUsers'] });
      setShowInviteModal(false);
      setInviteEmail('');
      setInviteRole('org:member');
    }
  });

  // Remove mutation
  const removeMutation = useMutation({
    mutationFn: removeUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminUsers'] });
      setConfirmRemove(null);
    }
  });

  // Update role mutation
  const updateRoleMutation = useMutation({
    mutationFn: updateUserRole,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminUsers'] });
      setConfirmRoleChange(null);
    }
  });

  const members = data?.members || [];
  const invitations = data?.invitations || [];

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    inviteMutation.mutate({ email: inviteEmail.trim(), role: inviteRole });
  };

  const handleRemove = (userId) => {
    removeMutation.mutate(userId);
  };

  const handleRoleChange = (userId, newRole) => {
    updateRoleMutation.mutate({ userId, role: newRole });
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'org:admin':
        return 'Admin';
      case 'org:member':
        return 'Member';
      default:
        return role;
    }
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'org:admin':
        return 'bg-purple-100 text-purple-800';
      case 'org:member':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-start">
          <div>
            <H3 className="mb-2">User Management</H3>
            <Text className="text-gray-600">Manage MarkoTM organization users</Text>
          </div>
        </div>
        <Card>
          <CardContent className="p-8 flex justify-center items-center">
            <Loader2 className="h-8 w-8 animate-spin text-[#00796b]" />
            <span className="ml-3 text-gray-600">Loading users...</span>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-start">
          <div>
            <H3 className="mb-2">User Management</H3>
            <Text className="text-gray-600">Manage MarkoTM organization users</Text>
          </div>
        </div>
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-red-600" />
              <div>
                <Text className="font-medium text-red-900">Failed to load users</Text>
                <Text className="text-sm text-red-700">{error.message}</Text>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="mt-4"
              onClick={() => refetch()}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <H3 className="mb-2">User Management</H3>
          <Text className="text-gray-600">
            Manage MarkoTM organization users and their roles
          </Text>
        </div>
        <Button
          onClick={() => setShowInviteModal(true)}
          className="bg-[#00796b] hover:bg-[#00695c] text-white"
        >
          <UserPlus className="h-4 w-4 mr-2" />
          Invite User
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <div className="bg-[#e0f2f1] p-2 rounded-full mr-3">
                <Users className="h-5 w-5 text-[#00796b]" />
              </div>
              <div>
                <Text className="text-sm font-medium text-gray-500">Total Members</Text>
                <Text className="text-lg font-bold">{members.length}</Text>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <div className="bg-purple-50 p-2 rounded-full mr-3">
                <Crown className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <Text className="text-sm font-medium text-gray-500">Admins</Text>
                <Text className="text-lg font-bold">
                  {members.filter(m => m.role === 'org:admin').length}
                </Text>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <div className="bg-amber-50 p-2 rounded-full mr-3">
                <Mail className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <Text className="text-sm font-medium text-gray-500">Pending Invitations</Text>
                <Text className="text-lg font-bold">{invitations.length}</Text>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Members List */}
      <Card>
        <CardHeader className="border-b border-gray-200">
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-[#00796b]" />
            Team Members ({members.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {members.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <Text>No team members yet</Text>
              <Text className="text-sm mt-1">Invite users to get started</Text>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {members.map((member) => (
                <div
                  key={member.userId}
                  className="p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-[#e0f2f1] flex items-center justify-center text-[#00796b] font-semibold">
                      {(member.name || member.email || '?')[0].toUpperCase()}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <Text className="font-medium text-gray-900">
                          {member.name || member.email}
                        </Text>
                        {member.isCurrentUser && (
                          <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                            You
                          </span>
                        )}
                      </div>
                      <Text className="text-sm text-gray-500">{member.email}</Text>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    {/* Role Badge / Selector */}
                    {member.isCurrentUser ? (
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getRoleBadgeClass(member.role)}`}>
                        {getRoleLabel(member.role)}
                      </span>
                    ) : (
                      <select
                        value={member.role}
                        onChange={(e) => setConfirmRoleChange({ userId: member.userId, name: member.name || member.email, newRole: e.target.value, currentRole: member.role })}
                        className="text-sm border border-gray-200 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-[#00796b]"
                        disabled={updateRoleMutation.isPending}
                      >
                        <option value="org:admin">Admin</option>
                        <option value="org:member">Member</option>
                      </select>
                    )}

                    {/* Remove Button */}
                    {!member.isCurrentUser && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-800 hover:bg-red-50"
                        onClick={() => setConfirmRemove(member)}
                        disabled={removeMutation.isPending}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pending Invitations */}
      {invitations.length > 0 && (
        <Card>
          <CardHeader className="border-b border-gray-200">
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-amber-600" />
              Pending Invitations ({invitations.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-gray-100">
              {invitations.map((invitation) => (
                <div
                  key={invitation.id}
                  className="p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-amber-50 flex items-center justify-center text-amber-600">
                      <Mail className="h-5 w-5" />
                    </div>
                    <div>
                      <Text className="font-medium text-gray-900">{invitation.email}</Text>
                      <Text className="text-sm text-gray-500">
                        Sent {formatDate(invitation.createdAt)}
                      </Text>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getRoleBadgeClass(invitation.role)}`}>
                      {getRoleLabel(invitation.role)}
                    </span>
                    <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                      Pending
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Invite User</h2>
                <button
                  onClick={() => setShowInviteModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <p className="text-sm text-gray-600 mt-1">
                Invite a new user to the MarkoTM organization
              </p>
            </div>

            <form onSubmit={handleInvite} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="user@example.com"
                  required
                  autoFocus
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#00796b] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role
                </label>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#00796b] focus:border-transparent"
                >
                  <option value="org:member">Member - Basic access</option>
                  <option value="org:admin">Admin - Full administrative access</option>
                </select>
              </div>

              <div className="bg-gray-50 rounded-md p-3">
                <div className="flex items-start gap-2">
                  <Mail className="h-4 w-4 text-gray-500 mt-0.5" />
                  <p className="text-sm text-gray-600">
                    An email invitation will be sent to join the MarkoTM organization.
                    They'll receive a link to sign up or sign in.
                  </p>
                </div>
              </div>

              {inviteMutation.error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-red-600" />
                    <p className="text-sm text-red-700">
                      {inviteMutation.error.response?.data?.message || inviteMutation.error.message}
                    </p>
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowInviteModal(false)}
                  disabled={inviteMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-[#00796b] hover:bg-[#00695c] text-white"
                  disabled={!inviteEmail.trim() || inviteMutation.isPending}
                >
                  {inviteMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="h-4 w-4 mr-2" />
                      Send Invitation
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Remove Confirmation Modal */}
      {confirmRemove && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
                <AlertCircle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Remove User</h2>
                <p className="text-sm text-gray-600">This action cannot be undone</p>
              </div>
            </div>

            <p className="text-gray-700 mb-6">
              Are you sure you want to remove <strong>{confirmRemove.name || confirmRemove.email}</strong> from the MarkoTM organization?
            </p>

            {removeMutation.error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-red-600" />
                  <p className="text-sm text-red-700">
                    {removeMutation.error.response?.data?.message || removeMutation.error.message}
                  </p>
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setConfirmRemove(null)}
                disabled={removeMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                onClick={() => handleRemove(confirmRemove.userId)}
                disabled={removeMutation.isPending}
              >
                {removeMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Removing...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Remove User
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Role Change Confirmation Modal */}
      {confirmRoleChange && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                <Shield className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Change Role</h2>
                <p className="text-sm text-gray-600">Update user permissions</p>
              </div>
            </div>

            <p className="text-gray-700 mb-6">
              Change <strong>{confirmRoleChange.name}</strong>'s role from{' '}
              <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${getRoleBadgeClass(confirmRoleChange.currentRole)}`}>
                {getRoleLabel(confirmRoleChange.currentRole)}
              </span>{' '}
              to{' '}
              <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${getRoleBadgeClass(confirmRoleChange.newRole)}`}>
                {getRoleLabel(confirmRoleChange.newRole)}
              </span>?
            </p>

            {updateRoleMutation.error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-red-600" />
                  <p className="text-sm text-red-700">
                    {updateRoleMutation.error.response?.data?.message || updateRoleMutation.error.message}
                  </p>
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setConfirmRoleChange(null)}
                disabled={updateRoleMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-[#00796b] hover:bg-[#00695c] text-white"
                onClick={() => handleRoleChange(confirmRoleChange.userId, confirmRoleChange.newRole)}
                disabled={updateRoleMutation.isPending}
              >
                {updateRoleMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Updating...
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Confirm Change
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
